/**
 * Resolves the orb's design-system color tokens into numeric RGB channels.
 *
 * The renderer needs per-frame alpha on the bloom and the orbiting comet, so it
 * builds `rgba()` strings numerically. That rules out the design system's
 * `alpha`/`lighten`/`darken` helpers, which return CSS `color-mix(in srgb, ...)`
 * expressions that canvas 2D cannot parse.
 */
import { lerp } from "./math";

export type Rgb = { r: number; g: number; b: number };

export type OrbPalette = {
  /** Ring stroke. Figma `PrimaryAndor/200` = `#ebf8f9`. */
  ring: Rgb;
  /** Translucent disc. Figma `#081B1D` @ 70%. */
  body: Rgb;
  /** Outer halo. Design-system `--theme-color-andor-400`. */
  glow: Rgb;
  /** Halo colour for the success state. Figma's gradient start, `#16c784`. */
  success: Rgb;
  /** Low stop of the fluid ramp. Defaults to `body`, overridden per state. */
  fluidLow: Rgb;
};

/**
 * Hex fallbacks, used when the design-system stylesheet has not loaded.
 * These are the literal values the tokens resolve to today.
 */
export const PALETTE_FALLBACK: OrbPalette = {
  ring: { r: 0xeb, g: 0xf8, b: 0xf9 },
  body: { r: 0x08, g: 0x1b, b: 0x1d },
  glow: { r: 0x62, g: 0xc6, b: 0xcd },
  success: { r: 0x16, g: 0xc7, b: 0x84 },
  fluidLow: { r: 0x08, g: 0x1b, b: 0x1d },
};

const TOKENS = {
  ring: "--wake-orb-ring",
  body: "--wake-orb-body",
  glow: "--wake-orb-glow",
  success: "--wake-orb-success",
  fluidLow: "--wake-orb-fluid-low",
} as const;

const HEX = /^#(?:([\da-f]{3})|([\da-f]{6}))$/i;
const RGB_FN = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i;

function parseColor(raw: string): Rgb | null {
  const value = raw.trim();
  if (!value) return null;

  const hex = HEX.exec(value);
  if (hex) {
    const short = hex[1];
    if (short) {
      return {
        r: parseInt(short[0] + short[0], 16),
        g: parseInt(short[1] + short[1], 16),
        b: parseInt(short[2] + short[2], 16),
      };
    }
    const long = hex[2];
    return {
      r: parseInt(long.slice(0, 2), 16),
      g: parseInt(long.slice(2, 4), 16),
      b: parseInt(long.slice(4, 6), 16),
    };
  }

  const fn = RGB_FN.exec(value);
  if (fn) {
    return { r: Number(fn[1]), g: Number(fn[2]), b: Number(fn[3]) };
  }

  return null;
}

/**
 * Reads the orb tokens off `el`. Call this on mount and on resize only —
 * `getComputedStyle` forces style resolution and must never run per frame.
 */
export function readPalette(el: Element): OrbPalette {
  const style = getComputedStyle(el);
  const resolve = (token: string, fallback: Rgb) =>
    parseColor(style.getPropertyValue(token)) ?? fallback;

  const body = resolve(TOKENS.body, PALETTE_FALLBACK.body);
  return {
    ring: resolve(TOKENS.ring, PALETTE_FALLBACK.ring),
    body,
    glow: resolve(TOKENS.glow, PALETTE_FALLBACK.glow),
    success: resolve(TOKENS.success, PALETTE_FALLBACK.success),
    // Falls back to the disc colour, matching the CSS default.
    fluidLow: resolve(TOKENS.fluidLow, body),
  };
}

export function rgba({ r, g, b }: Rgb, alpha: number): string {
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Straight sRGB blend — enough for two colours this close in luminance. */
export function mixRgb(from: Rgb, to: Rgb, t: number): Rgb {
  return {
    r: Math.round(lerp(from.r, to.r, t)),
    g: Math.round(lerp(from.g, to.g, t)),
    b: Math.round(lerp(from.b, to.b, t)),
  };
}
